import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash, X, ArrowCounterClockwise, AddressBook, CalendarCheck, Trophy, CaretRight, Envelope } from '@phosphor-icons/react';
import { classEntryAPI, refundAPI } from '../../utils/api';
import { toast } from 'sonner';
import { ContactProducerButton } from '../show/ContactProducerButton';
import { MailInCheckModal } from './MailInCheckModal';

/**
 * Calculate days-until / days-since for a show.
 * Returns { label, color, past } where:
 *   label is the short human string to render in a pill
 *   color is one of Tailwind-friendly hex strings
 *   past is true when the show's end date is before today
 */
const getShowTiming = (startDate, endDate) => {
  if (!startDate) return { label: null, color: '#6B7280', past: false };
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const start = new Date(startDate);
  const end = endDate ? new Date(endDate) : start;
  end.setHours(23, 59, 59, 999);

  const msPerDay = 24 * 60 * 60 * 1000;
  const daysTillStart = Math.round((start - today) / msPerDay);

  if (end < today) {
    const daysSince = Math.round((today - end) / msPerDay);
    return {
      label: daysSince === 0 ? 'Ended today' : `${daysSince}d ago`,
      color: '#6B7280',
      past: true,
    };
  }
  if (daysTillStart <= 0) {
    return { label: 'HAPPENING NOW', color: '#FF3B30', past: false };
  }
  if (daysTillStart === 1) {
    return { label: 'Tomorrow', color: '#FF9500', past: false };
  }
  if (daysTillStart <= 7) {
    return { label: `In ${daysTillStart} days`, color: '#FF9500', past: false };
  }
  return { label: `In ${daysTillStart} days`, color: '#0096FF', past: false };
};

export const RiderEntriesList = ({ myShowEntries, payingShowId, onPayNow, onRefresh }) => {
  const navigate = useNavigate();
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteReservation, setDeleteReservation] = useState(null); // {id, label, showId}
  const [cancelAllShow, setCancelAllShow] = useState(null); // {showId, showName, entries[], reservations[]}
  const [cancelAllBusy, setCancelAllBusy] = useState(false);
  const [refundConfirm, setRefundConfirm] = useState(null);
  const [refundReason, setRefundReason] = useState('');
  const [refundRequests, setRefundRequests] = useState([]);
  const [loadingRefund, setLoadingRefund] = useState(false);
  const [showPast, setShowPast] = useState(false);
  const [mailInCheckShow, setMailInCheckShow] = useState(null); // {showId, showName}

  useEffect(() => {
    refundAPI.myRequests().then(res => setRefundRequests(res.data || [])).catch(() => {});
  }, []);

  const { upcoming, past } = useMemo(() => {
    const up = [];
    const pa = [];
    for (const g of (myShowEntries || [])) {
      const timing = getShowTiming(g.show_start_date || g.show_date, g.show_end_date);
      const enriched = { ...g, __timing: timing };
      if (timing.past) pa.push(enriched); else up.push(enriched);
    }
    // Sort upcoming by soonest first, past by most-recent first
    up.sort((a, b) => new Date(a.show_start_date || a.show_date || 0) - new Date(b.show_start_date || b.show_date || 0));
    pa.sort((a, b) => new Date(b.show_end_date || b.show_start_date || 0) - new Date(a.show_end_date || a.show_start_date || 0));
    return { upcoming: up, past: pa };
  }, [myShowEntries]);

  if (!myShowEntries || myShowEntries.length === 0) return null;

  const hasPendingRefund = (showId) => refundRequests.some(r => r.show_id === showId && r.status === 'PENDING');
  const getRefundStatus = (showId) => refundRequests.find(r => r.show_id === showId);

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await classEntryAPI.remove(deleteConfirm.showId, deleteConfirm.classId, deleteConfirm.id);
      toast.success('Entry removed');
      setDeleteConfirm(null);
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove entry');
    }
  };

  const handleDeleteReservation = async () => {
    if (!deleteReservation) return;
    try {
      const api = (await import('../../utils/api')).default;
      await api.delete(`/spot-reservations/${deleteReservation.id}`);
      toast.success('Reservation cancelled');
      setDeleteReservation(null);
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel reservation');
    }
  };

  const handleCancelAll = async () => {
    if (!cancelAllShow) return;
    setCancelAllBusy(true);
    try {
      const api = (await import('../../utils/api')).default;
      const entryPromises = cancelAllShow.entries.map((e) =>
        classEntryAPI.remove(cancelAllShow.showId, e.class_id, e.id)
          .then(() => ({ ok: true }))
          .catch((err) => ({ ok: false, err }))
      );
      const resPromises = cancelAllShow.reservations.map((r) =>
        api.delete(`/spot-reservations/${r.id}`)
          .then(() => ({ ok: true }))
          .catch((err) => ({ ok: false, err }))
      );
      const results = await Promise.all([...entryPromises, ...resPromises]);
      const failed = results.filter((r) => !r.ok).length;
      const succeeded = results.length - failed;
      if (succeeded > 0) {
        toast.success(`Cancelled ${succeeded} pending item${succeeded === 1 ? '' : 's'}${failed > 0 ? ` (${failed} could not be cancelled)` : ''}`);
      }
      if (failed > 0 && succeeded === 0) {
        toast.error(`Failed to cancel — try again or contact the producer`);
      }
      setCancelAllShow(null);
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to cancel');
    } finally {
      setCancelAllBusy(false);
    }
  };

  const handleRefundRequest = async () => {
    if (!refundConfirm) return;
    setLoadingRefund(true);
    try {
      await refundAPI.request(refundConfirm.showId, refundReason);
      toast.success('Refund request submitted. The producer will review it.');
      setRefundConfirm(null);
      setRefundReason('');
      const res = await refundAPI.myRequests();
      setRefundRequests(res.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit refund request');
    } finally {
      setLoadingRefund(false);
    }
  };

  const renderShowGroup = (showGroup) => {
    const allPaid = showGroup.entries.every(e => e.payment_status === 'PAID');
    const anyPaid = showGroup.entries.some(e => e.payment_status === 'PAID');
    const pendingRefund = hasPendingRefund(showGroup.show_id);
    const refund = getRefundStatus(showGroup.show_id);
    const isRefunded = showGroup.entries.every(e => e.payment_status === 'REFUNDED');
    const t = showGroup.__timing || {};
    const canViewResults = showGroup.show_state === 'LIVE' || showGroup.show_state === 'COMPLETED' || t.past;
    const paidEntries = showGroup.entries.filter(e => e.payment_status === 'PAID' && !e.is_rollover);
    const firstPaidClassId = paidEntries[0]?.class_id;

    return (
      <div key={showGroup.show_id} className="bg-[#161616] border border-[#262626] rounded-lg p-5" data-testid={`show-entries-${showGroup.show_id}`}>
        <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={() => navigate(`/shows/${showGroup.show_id}`)}
                className="text-white font-bold text-base hover:text-[#0096FF] transition-colors text-left"
                style={{fontFamily: 'Outfit'}}
                data-testid={`my-entries-open-show-${showGroup.show_id}`}
              >
                {showGroup.show_name}
              </button>
              {t.label && (
                <span
                  className="text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider"
                  style={{ background: `${t.color}26`, color: t.color }}
                  data-testid={`show-timing-${showGroup.show_id}`}
                >
                  {t.label}
                </span>
              )}
            </div>
            <p className="text-[#9E9E9E] text-xs mt-0.5">
              {showGroup.show_start_date || showGroup.show_date}
              {showGroup.show_end_date && showGroup.show_end_date !== (showGroup.show_start_date || showGroup.show_date) ? ` – ${showGroup.show_end_date}` : ''}
              {showGroup.show_location ? ` • ${showGroup.show_location}` : ''}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {refund && refund.status === 'PENDING' && (
              <span className="px-2 py-1 rounded-full text-[10px] font-bold bg-[#FF9500]/15 text-[#FF9500]" data-testid={`refund-pending-${showGroup.show_id}`}>
                REFUND PENDING
              </span>
            )}
            {refund && refund.status === 'APPROVED' && (
              <span className="px-2 py-1 rounded-full text-[10px] font-bold bg-[#34C759]/15 text-[#34C759]" data-testid={`refund-approved-${showGroup.show_id}`}>
                REFUNDED
              </span>
            )}
            {refund && refund.status === 'DENIED' && (
              <span className="px-2 py-1 rounded-full text-[10px] font-bold bg-[#FF3B30]/15 text-[#FF3B30]" data-testid={`refund-denied-${showGroup.show_id}`} title={refund.deny_reason || 'Denied by producer'}>
                REFUND DENIED
              </span>
            )}
            <ContactProducerButton show={showGroup} compact />
            {canViewResults && firstPaidClassId && (
              <button
                onClick={() => navigate(`/shows/${showGroup.show_id}/results`)}
                className="flex items-center gap-1 px-3 py-1.5 text-xs font-bold bg-[#34C759]/10 border border-[#34C759]/30 text-[#34C759] rounded-lg hover:bg-[#34C759]/20 transition-colors"
                data-testid={`my-entries-results-${showGroup.show_id}`}
              >
                <Trophy size={12} weight="bold" /> Results
              </button>
            )}
            {!t.past && !refund && anyPaid && !isRefunded && (
              <button
                onClick={() => navigate(`/shows/${showGroup.show_id}`)}
                className="flex items-center gap-1 px-3 py-1.5 text-xs font-bold bg-[#0096FF]/10 border border-[#0096FF]/30 text-[#0096FF] rounded-lg hover:bg-[#0096FF]/20 transition-colors"
                data-testid={`my-entries-view-draw-${showGroup.show_id}`}
              >
                <CalendarCheck size={12} weight="bold" /> View Draw
              </button>
            )}
            {anyPaid && !pendingRefund && !isRefunded && !(refund && refund.status === 'APPROVED') && !t.past && (
              <button
                onClick={() => setRefundConfirm({ showId: showGroup.show_id, showName: showGroup.show_name })}
                className="flex items-center gap-1 px-3 py-1.5 text-xs font-bold bg-[#FF9500]/10 border border-[#FF9500]/30 text-[#FF9500] rounded-lg hover:bg-[#FF9500]/20 transition-colors"
                data-testid={`request-refund-btn-${showGroup.show_id}`}
              >
                <ArrowCounterClockwise size={12} weight="bold" /> Request Refund
              </button>
            )}
            {showGroup.has_pending && !t.past && (
              <div className="flex flex-col gap-1.5" data-testid={`pay-options-${showGroup.show_id}`}>
                <button
                  onClick={() => onPayNow(showGroup.show_id)}
                  disabled={payingShowId === showGroup.show_id}
                  className="px-4 py-2 bg-[#34C759] hover:bg-[#2FB350] text-white text-sm font-bold rounded-lg transition-colors disabled:opacity-50"
                  data-testid={`pay-now-btn-${showGroup.show_id}`}
                >
                  {payingShowId === showGroup.show_id ? 'Redirecting...' : 'Pay Online Now'}
                </button>
                <button
                  onClick={() => setMailInCheckShow({ showId: showGroup.show_id, showName: showGroup.show_name })}
                  className="px-3 py-2 bg-transparent border border-[#FF9500] hover:bg-[#FF9500]/10 text-[#FF9500] text-xs font-bold rounded-lg transition-colors inline-flex items-center justify-center gap-1.5"
                  data-testid={`pay-by-check-btn-${showGroup.show_id}`}
                >
                  <Envelope size={14} weight="bold" /> Pay by Mail-in Check
                </button>
                {(() => {
                  const pendingEntries = showGroup.entries.filter(e => ['UNPAID', 'PENDING'].includes(e.payment_status));
                  const pendingRes = (showGroup.reservations || []).filter(r => r.payment_status === 'PENDING');
                  const total = pendingEntries.length + pendingRes.length;
                  if (total === 0) return null;
                  return (
                    <button
                      onClick={() => setCancelAllShow({
                        showId: showGroup.show_id,
                        showName: showGroup.show_name,
                        entries: pendingEntries,
                        reservations: pendingRes,
                      })}
                      className="px-3 py-1.5 bg-transparent border border-[#FF3B30]/40 hover:bg-[#FF3B30]/10 text-[#FF3B30] text-[11px] font-bold rounded-lg transition-colors inline-flex items-center justify-center gap-1.5"
                      data-testid={`cancel-all-pending-btn-${showGroup.show_id}`}
                    >
                      <Trash size={11} weight="bold" /> Cancel All Pending ({total})
                    </button>
                  );
                })()}
              </div>
            )}
          </div>
        </div>
        <div className="space-y-1">
          {showGroup.entries.map((entry) => {
            const canDelete = ['UNPAID', 'PENDING'].includes(entry.payment_status);
            return (
              <div key={entry.id} className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-[#0A0A0A]/50 group">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-white text-sm truncate">{entry.class_name}</span>
                  {entry.rider_name && <span className="text-[#0096FF] text-[10px] font-semibold truncate">{entry.rider_name}</span>}
                  {entry.horse_name && <span className="text-[#9E9E9E] text-[10px] truncate">on {entry.horse_name}</span>}
                  {entry.is_rollover && <span className="text-[#FF9500] text-[10px] font-bold flex-shrink-0">ROLLOVER</span>}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[#9E9E9E] text-sm">${entry.entry_fee.toFixed(2)}</span>
                  {entry.awaiting_check && entry.payment_status !== 'PAID' && (() => {
                    const due = entry.check_due_date ? new Date(entry.check_due_date) : null;
                    const overdue = due && due < new Date();
                    return (
                      <span
                        className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${overdue ? 'bg-[#FF3B30]/15 text-[#FF3B30]' : 'bg-[#FACC15]/15 text-[#FACC15]'}`}
                        title={due ? `Check due ${due.toLocaleDateString()}` : 'Awaiting mail-in check'}
                        data-testid={`awaiting-check-badge-${entry.id}`}
                      >
                        {overdue ? 'CHECK OVERDUE' : 'CHECK · DUE'}
                      </span>
                    );
                  })()}
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                    entry.payment_status === 'PAID' ? 'bg-[#34C759]/15 text-[#34C759]' :
                    entry.payment_status === 'REFUNDED' ? 'bg-[#AF52DE]/15 text-[#AF52DE]' :
                    'bg-[#FF9500]/15 text-[#FF9500]'
                  }`}>{entry.payment_status}</span>
                  {canDelete && !t.past && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setDeleteConfirm({ id: entry.id, classId: entry.class_id, showId: showGroup.show_id, className: entry.class_name, riderName: entry.rider_name }); }}
                      className="p-1 opacity-0 group-hover:opacity-100 hover:bg-[#FF3B30]/10 rounded transition-all"
                      title="Remove entry"
                      data-testid={`delete-my-entry-${entry.id}`}
                    >
                      <Trash size={12} className="text-[#FF3B30]" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
          {/* Stalls & RV reservations — same row style as entries so riders see
              every charge they have for the show, not just classes. */}
          {(showGroup.reservations || []).length > 0 && (
            <div className="pt-2 mt-1 border-t border-[#262626]/60 space-y-1" data-testid={`my-reservations-${showGroup.show_id}`}>
              {showGroup.reservations.map((r) => {
                const canDelete = r.payment_status === 'PENDING' && !t.past;
                return (
                  <div key={r.id} className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-[#0A0A0A]/50 group">
                    <div className="flex items-center gap-2 min-w-0">
                      <span
                        className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded flex-shrink-0"
                        style={{ color: r.type === 'stall' ? '#FACC15' : '#5E5CE6', background: `${r.type === 'stall' ? '#FACC15' : '#5E5CE6'}1F` }}
                      >
                        {r.type === 'stall' ? 'STALL' : 'RV'}
                      </span>
                      <span className="text-white text-sm truncate">{r.label}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[#9E9E9E] text-sm">${Number(r.fee || 0).toFixed(2)}</span>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                        r.payment_status === 'PAID' ? 'bg-[#34C759]/15 text-[#34C759]' :
                        r.payment_status === 'CANCELLED' ? 'bg-[#9E9E9E]/15 text-[#9E9E9E]' :
                        'bg-[#FF9500]/15 text-[#FF9500]'
                      }`}>{r.payment_status}</span>
                      {canDelete && (
                        <button
                          onClick={(e) => { e.stopPropagation(); setDeleteReservation({ id: r.id, label: r.label, showId: showGroup.show_id }); }}
                          className="p-1 opacity-0 group-hover:opacity-100 hover:bg-[#FF3B30]/10 rounded transition-all"
                          title="Cancel this reservation"
                          data-testid={`delete-my-reservation-${r.id}`}
                        >
                          <Trash size={12} className="text-[#FF3B30]" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="mb-8" data-testid="my-entries-section">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-white" style={{fontFamily: 'Outfit'}}>My Entries</h2>
        {past.length > 0 && (
          <button
            onClick={() => setShowPast((v) => !v)}
            className="text-xs text-[#9E9E9E] hover:text-white transition-colors flex items-center gap-1"
            data-testid="toggle-past-entries"
          >
            {showPast ? 'Hide past shows' : `Show past shows (${past.length})`}
            <CaretRight size={11} weight="bold" className={`transition-transform ${showPast ? 'rotate-90' : ''}`} />
          </button>
        )}
      </div>

      {upcoming.length > 0 && (
        <div className="space-y-4" data-testid="upcoming-entries">
          {upcoming.map(renderShowGroup)}
        </div>
      )}

      {upcoming.length === 0 && past.length > 0 && (
        <p className="text-[#9E9E9E] text-sm italic mb-3" data-testid="no-upcoming">
          No upcoming shows. Toggle past shows on the right to see your history.
        </p>
      )}

      {showPast && past.length > 0 && (
        <div className="space-y-4 mt-5 pt-5 border-t border-[#262626]" data-testid="past-entries">
          <p className="text-[#9E9E9E] text-xs uppercase tracking-wider font-semibold">Past Shows</p>
          {past.map(renderShowGroup)}
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4" onClick={() => setDeleteConfirm(null)}>
          <div className="bg-[#161616] border border-[#262626] rounded-xl p-6 max-w-md w-full" onClick={e => e.stopPropagation()} data-testid="delete-my-entry-modal">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-bold text-white" style={{fontFamily: 'Outfit'}}>Remove Entry</h3>
              <button onClick={() => setDeleteConfirm(null)} className="p-1 hover:bg-[#262626] rounded"><X size={18} className="text-[#9E9E9E]" /></button>
            </div>
            <p className="text-[#9E9E9E] text-sm mb-1">
              Remove <span className="text-white font-semibold">{deleteConfirm.riderName}</span> from <span className="text-white font-semibold">{deleteConfirm.className}</span>?
            </p>
            <p className="text-[#555] text-xs mb-5">This will cancel the unpaid entry.</p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setDeleteConfirm(null)} className="px-4 py-2 rounded-md text-sm font-semibold text-[#9E9E9E] bg-[#262626] hover:bg-[#333] transition-colors" data-testid="delete-my-entry-cancel">Cancel</button>
              <button onClick={handleDelete} className="px-4 py-2 rounded-md text-sm font-semibold text-white bg-[#FF3B30] hover:bg-[#FF3B30]/80 transition-colors" data-testid="delete-my-entry-confirm">Remove</button>
            </div>
          </div>
        </div>
      )}
      {/* Reservation Cancel Confirmation */}
      {deleteReservation && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4" onClick={() => setDeleteReservation(null)}>
          <div className="bg-[#161616] border border-[#262626] rounded-xl p-6 max-w-md w-full" onClick={e => e.stopPropagation()} data-testid="delete-my-reservation-modal">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-bold text-white" style={{fontFamily: 'Outfit'}}>Cancel Reservation</h3>
              <button onClick={() => setDeleteReservation(null)} className="p-1 hover:bg-[#262626] rounded"><X size={18} className="text-[#9E9E9E]" /></button>
            </div>
            <p className="text-[#9E9E9E] text-sm mb-1">
              Cancel reservation for <span className="text-white font-semibold">{deleteReservation.label}</span>?
            </p>
            <p className="text-[#555] text-xs mb-5">This releases the spot so someone else can reserve it. You can re-reserve it later if it&apos;s still available.</p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setDeleteReservation(null)} className="px-4 py-2 rounded-md text-sm font-semibold text-[#9E9E9E] bg-[#262626] hover:bg-[#333] transition-colors" data-testid="delete-my-reservation-cancel">Keep It</button>
              <button onClick={handleDeleteReservation} className="px-4 py-2 rounded-md text-sm font-semibold text-white bg-[#FF3B30] hover:bg-[#FF3B30]/80 transition-colors" data-testid="delete-my-reservation-confirm">Release Spot</button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel All Pending Confirmation */}
      {cancelAllShow && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4" onClick={() => !cancelAllBusy && setCancelAllShow(null)}>
          <div className="bg-[#161616] border border-[#262626] rounded-xl p-6 max-w-md w-full" onClick={e => e.stopPropagation()} data-testid="cancel-all-pending-modal">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-bold text-white" style={{fontFamily: 'Outfit'}}>Cancel All Pending</h3>
              <button disabled={cancelAllBusy} onClick={() => setCancelAllShow(null)} className="p-1 hover:bg-[#262626] rounded disabled:opacity-40"><X size={18} className="text-[#9E9E9E]" /></button>
            </div>
            <p className="text-[#9E9E9E] text-sm mb-3">
              Remove ALL pending charges for <span className="text-white font-semibold">{cancelAllShow.showName}</span>?
            </p>
            <ul className="text-xs text-[#9E9E9E] mb-4 space-y-1 list-disc list-inside max-h-40 overflow-y-auto bg-[#0A0A0A] border border-[#1F1F1F] rounded p-2">
              {cancelAllShow.entries.map((e) => (
                <li key={`e-${e.id}`} className="truncate" data-testid={`cancel-all-entry-${e.id}`}>
                  {e.class_name}{e.rider_name ? ` · ${e.rider_name}` : ''}{e.horse_name ? ` on ${e.horse_name}` : ''}
                </li>
              ))}
              {cancelAllShow.reservations.map((r) => (
                <li key={`r-${r.id}`} className="truncate" data-testid={`cancel-all-reservation-${r.id}`}>{r.label}</li>
              ))}
            </ul>
            <p className="text-[#555] text-xs mb-5">This frees up your spots so other riders can grab them. Paid items are not touched.</p>
            <div className="flex gap-3 justify-end">
              <button disabled={cancelAllBusy} onClick={() => setCancelAllShow(null)} className="px-4 py-2 rounded-md text-sm font-semibold text-[#9E9E9E] bg-[#262626] hover:bg-[#333] transition-colors disabled:opacity-50" data-testid="cancel-all-pending-cancel">Keep Them</button>
              <button disabled={cancelAllBusy} onClick={handleCancelAll} className="px-4 py-2 rounded-md text-sm font-semibold text-white bg-[#FF3B30] hover:bg-[#FF3B30]/80 transition-colors disabled:opacity-50" data-testid="cancel-all-pending-confirm">
                {cancelAllBusy ? 'Cancelling…' : `Cancel All ${cancelAllShow.entries.length + cancelAllShow.reservations.length}`}
              </button>
            </div>
          </div>
        </div>
      )}



      {/* Refund Request Confirmation */}
      {refundConfirm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4" onClick={() => { setRefundConfirm(null); setRefundReason(''); }}>
          <div className="bg-[#161616] border border-[#262626] rounded-xl p-6 max-w-md w-full" onClick={e => e.stopPropagation()} data-testid="refund-request-modal">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-bold text-white" style={{fontFamily: 'Outfit'}}>Request Refund</h3>
              <button onClick={() => { setRefundConfirm(null); setRefundReason(''); }} className="p-1 hover:bg-[#262626] rounded"><X size={18} className="text-[#9E9E9E]" /></button>
            </div>
            <p className="text-[#9E9E9E] text-sm mb-3">
              Request a refund for all paid entries in <span className="text-white font-semibold">{refundConfirm.showName}</span>?
            </p>
            <p className="text-[#555] text-xs mb-3">The show producer will review your request. If approved, the refund will be returned to your original payment method.</p>
            <div className="mb-4">
              <label className="block text-xs text-[#9E9E9E] mb-1">Reason (optional)</label>
              <textarea
                value={refundReason}
                onChange={(e) => setRefundReason(e.target.value)}
                rows={2}
                placeholder="Why are you requesting a refund?"
                className="w-full px-3 py-2 bg-[#0A0A0A] border border-[#262626] rounded-md text-white text-sm placeholder:text-[#555] focus:outline-none focus:border-[#0096FF]"
                data-testid="refund-reason-input"
              />
            </div>
            <div className="flex gap-3 justify-end">
              <button onClick={() => { setRefundConfirm(null); setRefundReason(''); }} className="px-4 py-2 rounded-md text-sm font-semibold text-[#9E9E9E] bg-[#262626] hover:bg-[#333] transition-colors" data-testid="refund-request-cancel">Cancel</button>
              <button onClick={handleRefundRequest} disabled={loadingRefund} className="px-4 py-2 rounded-md text-sm font-semibold text-white bg-[#FF9500] hover:bg-[#FF9500]/80 transition-colors disabled:opacity-50" data-testid="refund-request-submit">
                {loadingRefund ? 'Submitting...' : 'Submit Request'}
              </button>
            </div>
          </div>
        </div>
      )}

      {mailInCheckShow && (
        <MailInCheckModal
          showId={mailInCheckShow.showId}
          showName={mailInCheckShow.showName}
          onClose={() => setMailInCheckShow(null)}
          onConfirmed={() => onRefresh?.()}
        />
      )}
    </div>
  );
};
